import { createContext, useContext, useState, useEffect } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing session and sync with backend
    const savedUser = localStorage.getItem('adminSession');
    const token = localStorage.getItem('token');
    
    if (savedUser && token) {
      try {
        const userData = JSON.parse(savedUser);
        setUser(userData);
        // Sync profile photo from backend if available
        syncProfileFromBackend(userData.id);
      } catch (error) {
        localStorage.removeItem('adminSession');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  const syncProfileFromBackend = async (userId) => {
    try {
      const BASE_URL = import.meta.env.VITE_API_URL || 'http://127.0.0.1:8000/api';
      
      // Add timeout untuk prevent hanging
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000); // 3 second timeout
      
      const response = await fetch(`${BASE_URL}/admin/current`, {
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const backendUser = data.data;
          setUser(prev => ({
            ...prev,
            profile_photo: backendUser.profile_photo || prev.profile_photo
          }));
          const savedUser = localStorage.getItem('adminSession');
          if (savedUser) {
            const userData = JSON.parse(savedUser);
            userData.profile_photo = backendUser.profile_photo;
            localStorage.setItem('adminSession', JSON.stringify(userData));
          }
        }
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        console.log('Profile sync timed out');
      } else {
        console.log('Could not sync profile from backend:', error);
      }
    }
  };

  const login = async (email, password) => {
    try {
      // Ambil kredensial dari localStorage
      const storedProfile = localStorage.getItem('adminProfile');
      const storedPassword = localStorage.getItem('adminPassword') || 'baru123';
      
      let validEmail = 'emiliawati1@sewaapartemen.com'; // default
      
      if (storedProfile) {
        try {
          const profile = JSON.parse(storedProfile);
          validEmail = profile.email;
        } catch (error) {
          // Gunakan default jika parsing gagal
        }
      }
      
      // Cek kredensial
      if (email === validEmail && password === storedPassword) {
        // Buat user data langsung tanpa menunggu backend
        const userData = {
          id: 1,
          name: 'Emiliawati',
          email: validEmail,
          phone: '+62 812-3456-7890',
          role: 'Administrator',
          profile_photo: null // Will be fetched in background
        };
        
        // Simpan session
        const token = 'admin-token-' + Date.now();
        localStorage.setItem('token', token);
        localStorage.setItem('adminSession', JSON.stringify(userData));
        setUser(userData);
        
        // Fetch profile photo in background (non-blocking)
        setTimeout(() => {
          syncProfileFromBackend(1).catch(() => {
            // Silent fail - user is already logged in
          });
        }, 100);
        
        return { success: true };
      }
      
      return { success: false, error: 'The provided credentials are incorrect.' };
    } catch (error) {
      return { success: false, error: 'Login failed. Please try again.' };
    }
  };

  const updateProfile = (newProfileData) => {
    try {
      const updatedUser = { ...user, ...newProfileData };
      setUser(updatedUser);
      localStorage.setItem('adminSession', JSON.stringify(updatedUser));
      
      // Update adminProfile untuk login
      const profileData = {
        name: updatedUser.name,
        email: updatedUser.email,
        phone: updatedUser.phone
      };
      localStorage.setItem('adminProfile', JSON.stringify(profileData));
      
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to update profile' };
    }
  };

  const changePassword = (currentPassword, newPassword) => {
    try {
      const storedPassword = localStorage.getItem('adminPassword') || 'baru123';
      
      if (currentPassword !== storedPassword) {
        return { success: false, error: 'Current password is incorrect' };
      }
      
      localStorage.setItem('adminPassword', newPassword);
      return { success: true };
    } catch (error) {
      return { success: false, error: 'Failed to change password' };
    }
  };

  const logout = () => {
    try {
      // Trigger loading animation
      window.dispatchEvent(new CustomEvent('admin-logout'));
      
      setUser(null);
      localStorage.removeItem('adminSession');
      localStorage.removeItem('token');
    } catch (error) {
      // Fallback if localStorage fails
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, updateProfile, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
};